import { differenceInDays } from "date-fns";
import {
	AlertTriangleIcon,
	CalendarIcon,
	CheckCircleIcon,
	ClockIcon,
	CreditCardIcon,
	HandbagIcon,
	PauseCircleIcon,
	XCircleIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { LEMON_CHECKOUT_URL, LEMON_CUSTOMER_PORTAL_URL } from "@/config/lemon";
import { useSubscriptionStatus } from "@/hooks/use-subscription-status";
import { formatDate } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { SubscriptionsStatusOptions } from "@/types/pocketbase.ts";

const DAYS_PER_MONTH = 30;
const PERCENTAGE_MULTIPLIER = 100;
const ENDING_SOON_DAYS_THRESHOLD = 7;

function calculateProgress(dayLeft: number, isExpired: boolean): number {
	return isExpired ? PERCENTAGE_MULTIPLIER : ((DAYS_PER_MONTH - dayLeft) / DAYS_PER_MONTH) * PERCENTAGE_MULTIPLIER;
}

type StatusConfig = {
	icon: React.ComponentType<{ className?: string }>;
	variant: "default" | "secondary" | "destructive" | "outline" | "ghost";
	progressClassName?: string;
	isExpired?: boolean;
};

const STATUS_CONFIG: Record<SubscriptionsStatusOptions, StatusConfig> = {
	[SubscriptionsStatusOptions.on_trial]: {
		icon: ClockIcon,
		variant: "default",
	},
	[SubscriptionsStatusOptions.active]: {
		icon: CheckCircleIcon,
		variant: "secondary",
	},
	[SubscriptionsStatusOptions.paused]: {
		icon: PauseCircleIcon,
		variant: "secondary",
	},
	[SubscriptionsStatusOptions.past_due]: {
		icon: AlertTriangleIcon,
		variant: "default",
		progressClassName: "[&>div]:bg-yellow-500",
	},
	[SubscriptionsStatusOptions.unpaid]: {
		icon: CreditCardIcon,
		variant: "destructive",
		progressClassName: "[&>div]:bg-destructive",
	},
	[SubscriptionsStatusOptions.cancelled]: {
		icon: XCircleIcon,
		variant: "destructive",
		progressClassName: "[&>div]:bg-destructive",
	},
	[SubscriptionsStatusOptions.ends_at]: {
		icon: ClockIcon,
		variant: "default",
		progressClassName: "[&>div]:bg-yellow-500",
	},
	[SubscriptionsStatusOptions.expired]: {
		icon: XCircleIcon,
		variant: "destructive",
		isExpired: true,
		progressClassName: "[&>div]:bg-destructive",
	},
};

function SubscriptionTrackerHeader({ status, planName }: { status: SubscriptionsStatusOptions; planName?: string }) {
	const { t } = useTranslation();
	const config = STATUS_CONFIG[status];
	const StatusIcon = config.icon;

	const title = (() => {
		switch (status) {
			case SubscriptionsStatusOptions.on_trial:
				return `${planName} ${t(($) => $.modules.settings.billing.subscriptionTracker.trial)}`;
			case SubscriptionsStatusOptions.active:
				return t(($) => $.modules.settings.billing.subscriptionTracker.active);
			case SubscriptionsStatusOptions.paused:
				return t(($) => $.modules.settings.subscription.paused.title);
			case SubscriptionsStatusOptions.past_due:
				return t(($) => $.modules.settings.subscription.past_due.title);
			case SubscriptionsStatusOptions.unpaid:
				return t(($) => $.modules.settings.subscription.unpaid.title);
			case SubscriptionsStatusOptions.cancelled:
				return t(($) => $.modules.settings.subscription.cancelled.title);
			case SubscriptionsStatusOptions.ends_at:
				return t(($) => $.modules.settings.billing.subscriptionTracker.endsAt);
			case SubscriptionsStatusOptions.expired:
				return t(($) => $.modules.settings.subscription.expired.title);
			default:
				return t(($) => $.modules.settings.billing.subscriptionTracker.title);
		}
	})();

	const description = (() => {
		switch (status) {
			case SubscriptionsStatusOptions.on_trial:
				return t(($) => $.modules.settings.billing.subscriptionTracker.trialDescription);
			case SubscriptionsStatusOptions.active:
				return t(($) => $.modules.settings.billing.subscriptionTracker.activeDescription);
			case SubscriptionsStatusOptions.paused:
				return t(($) => $.modules.settings.subscription.paused.message);
			case SubscriptionsStatusOptions.past_due:
				return t(($) => $.modules.settings.subscription.past_due.message);
			case SubscriptionsStatusOptions.unpaid:
				return t(($) => $.modules.settings.subscription.unpaid.message);
			case SubscriptionsStatusOptions.cancelled:
				return t(($) => $.modules.settings.subscription.cancelled.message);
			case SubscriptionsStatusOptions.ends_at:
				return t(($) => $.modules.settings.billing.subscriptionTracker.endsAtDescription);
			case SubscriptionsStatusOptions.expired:
				return t(($) => $.modules.settings.subscription.expired.message);
			default:
				return "";
		}
	})();

	return (
		<CardHeader className="px-4">
			<CardTitle
				className={cn(
					"flex items-center gap-2 text-sm",
					(config.variant === "destructive" || config.isExpired) && "text-destructive"
				)}
			>
				<StatusIcon className="size-4" />
				{title}
			</CardTitle>
			<CardDescription>{description}</CardDescription>
		</CardHeader>
	);
}

function SubscriptionTrackerContent({
	status,
	dayLeft,
	startDate,
	endsAt,
}: {
	status: SubscriptionsStatusOptions;
	dayLeft: number;
	startDate?: Date;
	endsAt?: Date;
}) {
	const { t } = useTranslation();
	const config = STATUS_CONFIG[status];
	const isTrial = status === SubscriptionsStatusOptions.on_trial;
	const isEndingSoon = dayLeft <= ENDING_SOON_DAYS_THRESHOLD && dayLeft >= 0;
	const isExpired = status === SubscriptionsStatusOptions.expired || dayLeft <= 0;
	const progressValue = calculateProgress(dayLeft, isExpired);
	const actionUrl = isExpired ? LEMON_CUSTOMER_PORTAL_URL : LEMON_CHECKOUT_URL;

	const showProgress = isTrial || isEndingSoon || isExpired;

	const statusText = (() => {
		if (isExpired) {
			return t(($) => $.modules.settings.billing.subscriptionTracker.expired);
		}
		if (isTrial) {
			return `${dayLeft} ${t(($) => $.modules.settings.billing.subscriptionTracker.daysLeft)}`;
		}
		if (isEndingSoon) {
			return `${dayLeft} ${t(($) => $.modules.settings.billing.subscriptionTracker.daysLeft)}`;
		}
		if (status === SubscriptionsStatusOptions.active) {
			return t(($) => $.modules.settings.billing.subscriptionTracker.activeStatus);
		}
		if (status === SubscriptionsStatusOptions.paused) {
			return t(($) => $.modules.settings.billing.subscriptionTracker.pausedStatus);
		}
		return "";
	})();

	const isManageButton =
		isExpired ||
		status === SubscriptionsStatusOptions.paused ||
		status === SubscriptionsStatusOptions.cancelled ||
		(status === SubscriptionsStatusOptions.active && dayLeft <= 0);

	const buttonLabel = isManageButton
		? t(($) => $.modules.settings.subscription.button)
		: t(($) => $.modules.settings.billing.subscriptionTracker.upgrade);

	const showEndDate = isTrial || (status === SubscriptionsStatusOptions.active && isEndingSoon);

	return (
		<CardContent className="px-4">
			<div className="space-y-3">
				<div className="flex items-center justify-between gap-2 font-semibold text-sm">
					<span className={cn(isExpired && "text-destructive")}>{statusText}</span>
				</div>
				{showProgress && <Progress className={cn(config.progressClassName)} value={progressValue} />}
				{startDate && (
					<div className="flex items-center gap-2 text-muted-foreground text-xs">
						<CalendarIcon className="size-4" />
						<span>
							{t(($) => $.modules.settings.billing.subscriptionTracker.startedIn)}{" "}
							{formatDate(startDate, {
								year: "numeric",
								month: "long",
								day: "numeric",
							})}
						</span>
					</div>
				)}
				{endsAt && showEndDate && (
					<div className="flex items-center gap-2 text-muted-foreground text-xs">
						<CalendarIcon className="size-4" />
						<span>
							{t(($) => $.modules.settings.billing.subscriptionTracker.endsAtDate)}{" "}
							{formatDate(endsAt, {
								year: "numeric",
								month: "long",
								day: "numeric",
							})}
						</span>
					</div>
				)}
			</div>
			<a href={actionUrl} rel="noopener noreferrer" target="_blank">
				<Button
					className="mt-4 w-full"
					size="sm"
					variant={isExpired || config.variant === "destructive" ? "destructive" : "default"}
				>
					{buttonLabel}
				</Button>
			</a>
		</CardContent>
	);
}

export function SubscriptionTracker({ isExpanded }: { isExpanded: boolean }) {
	const { t } = useTranslation();

	const { status, trialEndsAt, endsAt, created, expand, isLoading, isActive } = useSubscriptionStatus();

	if (isLoading) return null;
	if (!status) return null;

	const planName = expand?.plan.name;
	const startDate = created ? new Date(created) : undefined;
	let endDate: Date | undefined;
	if (endsAt) {
		endDate = new Date(endsAt);
	} else if (trialEndsAt) {
		endDate = new Date(trialEndsAt);
	}
	const dayLeft = endDate ? differenceInDays(endDate, new Date()) : Number.POSITIVE_INFINITY;

	if (isActive && isExpanded) {
		return (
			<Card className="gap-2 py-4 shadow-none">
				<SubscriptionTrackerHeader planName={planName} status={status} />
				<SubscriptionTrackerContent dayLeft={dayLeft} endsAt={endDate} startDate={startDate} status={status} />
			</Card>
		);
	}

	if (isActive) return null;

	if (isExpanded) {
		return (
			<Card
				className={cn(
					"gap-2 py-4 shadow-none",
					STATUS_CONFIG[status]?.isExpired && "border-destructive bg-destructive/5"
				)}
			>
				<SubscriptionTrackerHeader planName={planName} status={status} />
				<SubscriptionTrackerContent dayLeft={dayLeft} endsAt={endDate} startDate={startDate} status={status} />
			</Card>
		);
	}

	return (
		<Button
			asChild
			className="w-full"
			color="primary"
			onClick={() => window.open(LEMON_CHECKOUT_URL, "_blank")}
			size="sm"
			title={t(($) => $.modules.settings.billing.subscriptionTracker.upgrade)}
			variant="outline"
		>
			<span className="flex items-center gap-2">
				<HandbagIcon className="size-4" />
				<span className="sr-only">{t(($) => $.modules.settings.billing.subscriptionTracker.upgrade)}</span>
			</span>
		</Button>
	);
}
